import { ESCURO } from "assets/coresTema";
// Utilitários da Análise de Atendimento (tela). A planilha exportada não usa este arquivo.

const formatoInteiro = new Intl.NumberFormat("pt-BR");
const formatoDecimal = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const formatoPercentual = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatoPercentualCurto = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 1 });

export const formatarNumero = (valor) => formatoInteiro.format(valor || 0);
export const formatarDecimal = (valor) => (valor === null || valor === undefined || Number.isNaN(valor) ? "-" : formatoDecimal.format(valor));
export const formatarPercentual = (valor) => (valor === null || valor === undefined ? "" : formatoPercentual.format(valor));
export const formatarPercentualCurto = (valor) => (valor === null || valor === undefined ? "" : formatoPercentualCurto.format(valor));

// "1. Janeiro.2026" -> "Jan/2026"
export const rotuloCurtoMes = (rotulo) => {
    const semNumero = String(rotulo).split(". ")[1] || String(rotulo);
    const [nome, ano] = semNumero.split(".");
    return ano ? `${nome.slice(0, 3)}/${ano}` : nome;
};

// Clientes como vêm do backend: na carteira de cobrança (Extra), os grupos GETNET, CIELO, PLUXEE, UMC e COBEXTRA (os
// demais); na jurídica, o nome de cada cliente. Só os que estão nos dados, que já vêm só das carteiras e clientes que o
// usuário vê
const emOrdem = (nomes) => [...new Set(nomes.filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));

/** Clientes da análise de atendimento e dos acordos (linhas do /acordos), em ordem alfabética. */
export const clientesDosDados = (dados, linhasAcordos) => emOrdem([
    ...((dados && dados.clientes) || []).map(c => c.cliente),
    ...(linhasAcordos || []).map(l => l.cliente),
]);

/** Clientes que aparecem nas linhas dos blocos (pessoas) da análise. */
export const clientesDosBlocos = (blocos) => emOrdem((blocos || []).flatMap(b => (b.linhas || []).map(l => l.cliente)));

// Filtros da tela (cliente, negociador, coordenador): um valor, uma lista (vários escolhidos) ou vazio (todos)
export const paraLista = (filtro) => {
    if (Array.isArray(filtro)) return filtro;
    return filtro ? [filtro] : [];
};
export const temFiltro = (filtro) => paraLista(filtro).length > 0;
export const passaNoFiltro = (filtro, valor) => !temFiltro(filtro) || paraLista(filtro).includes(valor);

// Cor identifica pessoa (negociador/coordenador); clientes são identificados pelo nome. Toda pessoa tem a sua cor (sem
// "Outros"), na ordem de atendimentos: as 5 primeiras com os tons principais da paleta categórica (skill dataviz: azul,
// laranja, verde-água, amarelo, vermelho), da 6ª à 10ª os mesmos tons mais escuros (claro) / mais claros (escuro), e só
// da 11ª em diante magenta, violeta e verde, que são os tons das medidas (CORES_MEDIDAS). Tons da mesma família ficam
// 5 posições longe um do outro, então não encostam na barra empilhada. Passando de 16 pessoas, as cores se repetem: a
// pessoa é identificada pelo nome (legenda com realce, tooltip, Ver detalhes), não só pela cor.
const CORES_PESSOAS = {
    light: {
        cores: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e34948", "#164f94", "#a3431c", "#0d6b4a", "#8f6100", "#9c2a29",
            "#e87ba4", "#6250d6", "#008300", "#a8456c", "#3a2d99", "#0a5a0a"],
        semDados: "#898781",
    },
    dark: {
        cores: ["#3987e5", "#d95926", "#199e70", "#c98500", "#e66767", "#9cc4f5", "#f0a07f", "#74d1af", "#e8c066", "#f4adad",
            "#d55181", "#9085e9", "#008300", "#eea3c0", "#c6c0f6", "#6fbf6f"],
        semDados: "#b5b4ab",
    },
};

/**
 * Cores por pessoa no período: ordem por atendimentos (quem só formalizou acordo, em `extras`, vem depois, por nome).
 * O ranking não depende dos filtros de cliente/pessoa, então filtrar não repinta ninguém.
 */
export function coresPorPessoa(blocos, darkMode, extras = []) {
    const paleta = CORES_PESSOAS[darkMode ? "dark" : "light"];
    const ordem = [...blocos]
        .sort((a, b) => (b.atendimentosTotal || 0) - (a.atendimentosTotal || 0) || a.nome.localeCompare(b.nome))
        .map(b => b.nome);
    const jaTem = new Set(ordem);
    extras.filter(nome => nome && !jaTem.has(nome)).sort((a, b) => a.localeCompare(b)).forEach(nome => ordem.push(nome));
    const posicao = new Map(ordem.map((nome, i) => [nome, i]));
    return {
        ordem,
        cor: (nome) => (posicao.has(nome) ? paleta.cores[posicao.get(nome) % paleta.cores.length] : paleta.semDados),
    };
}

/**
 * Participação de cada pessoa nas fichas acionadas de cada cliente (cada cliente soma 100%).
 * Base: fichas acionadas mês a mês, a mesma do % de tempo.
 */
export function participacaoPorCliente(blocos, cliente) {
    const partes = blocos
        .map(b => {
            const linha = b.linhas.find(l => l.cliente === cliente);
            return { nome: b.nome, quantidade: linha ? linha.quantidadeTotal : 0 };
        })
        .filter(p => p.quantidade > 0);
    const total = partes.reduce((acc, p) => acc + p.quantidade, 0);
    return { total, partes: partes.map(p => ({ ...p, percentual: total > 0 ? p.quantidade / total : 0 })) };
}

// Medidas usam cores que as pessoas só ganham da 11ª em diante (CORES_PESSOAS), para não se confundirem com elas. Negociações conferida contra fichas e
// atendimentos (OKLab, também em protan/deutan/tritan): menor ΔE 10,3 no claro e 9,1 no escuro; contraste 5,4 e 3,2.
const CORES_MEDIDAS = {
    fichas: { light: "#4a3aa7", dark: "#9085e9" },
    atendimentos: { light: "#008300", dark: "#008300" },
    negociacoes: { light: "#b4357a", dark: "#e07ab1" },
};

export const corMedida = (medida, darkMode) => CORES_MEDIDAS[medida][darkMode ? "dark" : "light"];

export const temaGrafico = (darkMode) => ({
    texto: darkMode ? "rgba(255, 255, 255, 0.9)" : "#344767",
    textoSecundario: darkMode ? "rgba(255, 255, 255, 0.65)" : "#7b809a",
    grade: darkMode ? "rgba(255, 255, 255, 0.10)" : "#e9ecef",
    superficie: darkMode ? ESCURO.grafico : "#fafafa",
    borda: darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0",
});

// Texto legível sobre um fundo colorido (rótulos dentro das barras)
export const corTextoSobre = (hex) => {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map(i => parseInt(valor.substring(i, i + 2), 16) / 255)
        .map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    const luminancia = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminancia > 0.3 ? "#0b0b0b" : "#ffffff";
};

const somar = (lista) => lista.reduce((acc, v) => acc + (v || 0), 0);
const somarPorMes = (listas, qtdMeses) => Array.from({ length: qtdMeses }, (_, i) => somar(listas.map(l => (l ? l[i] : 0))));

/**
 * Soma os blocos de várias pessoas num único bloco (mesmo formato do backend),
 * recalculando o % de tempo por cliente. Usado para a visão "Todos".
 */
export function consolidarBlocos(blocos, nome, qtdMeses) {
    const porCliente = {};
    blocos.forEach(bloco => bloco.linhas.forEach(linha => {
        const acc = porCliente[linha.cliente] || (porCliente[linha.cliente] = { quantidades: [], atendimentos: [], atendimentosTotal: 0, fichasPeriodo: 0 });
        acc.quantidades.push(linha.quantidades);
        acc.atendimentos.push(linha.atendimentos || []);
        acc.atendimentosTotal += linha.atendimentosTotal || 0;
        acc.fichasPeriodo += linha.fichasPeriodo || 0;
    }));

    const clientes = Object.keys(porCliente).sort();
    const quantidadesPorCliente = clientes.map(c => somarPorMes(porCliente[c].quantidades, qtdMeses));
    const totaisMes = somarPorMes(quantidadesPorCliente, qtdMeses);
    const totalGeral = somar(totaisMes);

    const linhas = clientes.map((cliente, idx) => {
        const quantidades = quantidadesPorCliente[idx];
        const quantidadeTotal = somar(quantidades);
        return {
            cliente,
            quantidades,
            percentuais: quantidades.map((q, i) => (q > 0 ? q / totaisMes[i] : null)),
            quantidadeTotal,
            percentualTotal: totalGeral > 0 ? quantidadeTotal / totalGeral : null,
            atendimentos: somarPorMes(porCliente[cliente].atendimentos, qtdMeses),
            atendimentosTotal: porCliente[cliente].atendimentosTotal,
            fichasPeriodo: porCliente[cliente].fichasPeriodo,
        };
    });

    return {
        nome,
        linhas,
        totaisMes,
        totalGeral,
        atendimentosMes: somarPorMes(linhas.map(l => l.atendimentos), qtdMeses),
        atendimentosTotal: somar(linhas.map(l => l.atendimentosTotal)),
        fichasPeriodo: somar(linhas.map(l => l.fichasPeriodo)),
    };
}

export const clientePrincipal = (bloco) => bloco.linhas.reduce(
    (maior, linha) => (!maior || (linha.percentualTotal || 0) > (maior.percentualTotal || 0) ? linha : maior),
    null
);

/**
 * Números de uma pessoa no escopo do filtro de cliente (um ou vários).
 * fichasMes soma os clientes: cada ficha é de um único cliente, então não há duplicidade.
 */
export function numerosDaPessoa(bloco, cliente, qtdMeses) {
    const linhas = bloco.linhas.filter(l => passaNoFiltro(cliente, l.cliente));
    return {
        fichas: somar(linhas.map(l => l.fichasPeriodo)),
        atendimentos: somar(linhas.map(l => l.atendimentosTotal)),
        fichasMes: somarPorMes(linhas.map(l => l.quantidades), qtdMeses),
        atendimentosMes: somarPorMes(linhas.map(l => l.atendimentos), qtdMeses),
    };
}

/**
 * Indicadores do topo da tela.
 * Sem pessoa selecionada usa os totais gerais por cliente do backend, que contam cada ficha uma vez
 * mesmo quando acionada por mais de um negociador.
 */
export function calcularIndicadores(dados, blocos, pessoas, cliente) {
    const qtdMeses = dados?.meses?.length || 0;
    if (!dados || qtdMeses === 0) {
        return null;
    }

    let numeros;
    let blocoDedicacao;
    const escolhidas = paraLista(pessoas);
    if (escolhidas.length) {
        // Quem só formalizou acordo no período (sem atendimento) não tem bloco: entra com atendimento zerado. Com várias
        // pessoas, soma as de cada uma: a ficha acionada por duas delas conta para as duas
        const blocosEscolhidos = blocos.filter(b => escolhidas.includes(b.nome));
        const porPessoa = blocosEscolhidos.map(b => numerosDaPessoa(b, cliente, qtdMeses));
        numeros = {
            fichas: somar(porPessoa.map(n => n.fichas)),
            atendimentos: somar(porPessoa.map(n => n.atendimentos)),
            fichasMes: somarPorMes(porPessoa.map(n => n.fichasMes), qtdMeses),
            atendimentosMes: somarPorMes(porPessoa.map(n => n.atendimentosMes), qtdMeses),
        };
        blocoDedicacao = blocosEscolhidos.length === 1 ? blocosEscolhidos[0] : consolidarBlocos(blocosEscolhidos, "Escolhidos", qtdMeses);
    } else {
        const totais = (dados.clientes || []).filter(c => passaNoFiltro(cliente, c.cliente));
        numeros = {
            fichas: somar(totais.map(c => c.fichasPeriodo)),
            atendimentos: somar(totais.map(c => c.atendimentosPeriodo)),
            fichasMes: somarPorMes(totais.map(c => c.fichasMes), qtdMeses),
            atendimentosMes: somarPorMes(totais.map(c => c.atendimentosMes), qtdMeses),
        };
        blocoDedicacao = consolidarBlocos(blocos, "Todos", qtdMeses);
    }

    const percentuaisPorCliente = {};
    blocoDedicacao.linhas.forEach(l => { percentuaisPorCliente[l.cliente] = l.percentualTotal || 0; });

    return {
        ...numeros,
        mediaPorFicha: numeros.fichas > 0 ? numeros.atendimentos / numeros.fichas : null,
        principal: clientePrincipal(blocoDedicacao),
        percentuaisPorCliente,
    };
}

// ==================== Período (Data Inicial / Data Final, "yyyy-MM-dd") ====================

// Mesmo limite do backend (AnaliseTempoService.MAX_MESES_PERIODO)
export const MAX_MESES_PERIODO = 12;

const paraData = (iso) => {
    const [ano, mes, dia] = iso.split("-").map(Number);
    return new Date(ano, mes - 1, dia);
};
const paraIso = (data) => `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
const ultimoDiaDoMes = (data) => new Date(data.getFullYear(), data.getMonth() + 1, 0).getDate();
const mesesTocados = (inicio, fim) => (fim.getFullYear() - inicio.getFullYear()) * 12 + fim.getMonth() - inicio.getMonth() + 1;

/** Período inicial da tela: do primeiro dia do trimestre atual até hoje. */
export function periodoPadrao() {
    const hoje = new Date();
    return {
        dataInicial: paraIso(new Date(hoje.getFullYear(), Math.floor(hoje.getMonth() / 3) * 3, 1)),
        dataFinal: paraIso(hoje),
    };
}

/** Mensagem de erro do período, ou null se ele pode ser consultado. */
export function validarPeriodo(dataInicial, dataFinal) {
    const valida = (iso) => /^\d{4}-\d{2}-\d{2}$/.test(iso || "") && Number(iso.slice(0, 4)) >= 2000;
    if (!dataInicial || !dataFinal) return "Informe a Data Inicial e a Data Final.";
    if (!valida(dataInicial) || !valida(dataFinal)) return "Data inválida.";
    const inicio = paraData(dataInicial);
    const fim = paraData(dataFinal);
    if (fim < inicio) return "A Data Final deve ser igual ou posterior à Data Inicial.";
    if (mesesTocados(inicio, fim) > MAX_MESES_PERIODO) return `O período pode ter no máximo ${MAX_MESES_PERIODO} meses.`;
    return null;
}

/**
 * Período anterior equivalente, para as variações dos cards. Meses inteiros (ex.: 01/07 a 30/09) voltam a mesma
 * quantidade de meses (01/04 a 30/06); fora isso, a mesma quantidade de dias imediatamente antes.
 */
export function periodoAnterior(dataInicial, dataFinal) {
    const inicio = paraData(dataInicial);
    const fim = paraData(dataFinal);
    if (inicio.getDate() === 1 && fim.getDate() === ultimoDiaDoMes(fim)) {
        const qtdMeses = mesesTocados(inicio, fim);
        return {
            dataInicial: paraIso(new Date(inicio.getFullYear(), inicio.getMonth() - qtdMeses, 1)),
            dataFinal: paraIso(new Date(inicio.getFullYear(), inicio.getMonth(), 0)),
        };
    }
    const dias = Math.round((fim - inicio) / 86400000) + 1;
    return {
        dataInicial: paraIso(new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() - dias)),
        dataFinal: paraIso(new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() - 1)),
    };
}

/** "01/07 a 30/09/2026" (mesmo ano) ou "15/11/2025 a 10/02/2026". */
export function formatarPeriodo(dataInicial, dataFinal) {
    const [anoIni, mesIni, diaIni] = dataInicial.split("-");
    const [anoFim, mesFim, diaFim] = dataFinal.split("-");
    const inicio = anoIni === anoFim ? `${diaIni}/${mesIni}` : `${diaIni}/${mesIni}/${anoIni}`;
    return `${inicio} a ${diaFim}/${mesFim}/${anoFim}`;
}

/** Variação percentual entre dois valores; null quando não há base de comparação. */
export const variacaoPercentual = (atual, anterior) => (
    atual === null || atual === undefined || !anterior ? null : (atual - anterior) / anterior
);
